/**
 * Shared password rules — same as Account Settings → Password & Security
 * (PasswordStrengthChecker). Do not invent divergent registration rules.
 */

export type PasswordStrength = 'weak' | 'medium' | 'strong' | 'very-strong'

export type PasswordRequirement = {
  id: string
  labelKey: string
  test: (password: string) => boolean
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    id: 'length',
    labelKey: 'password.reqLength',
    test: (pwd) => pwd.length >= 8,
  },
  {
    id: 'lowercase',
    labelKey: 'password.reqLowercase',
    test: (pwd) => /[a-z]/.test(pwd),
  },
  {
    id: 'uppercase',
    labelKey: 'password.reqUppercase',
    test: (pwd) => /[A-Z]/.test(pwd),
  },
  {
    id: 'number',
    labelKey: 'password.reqNumber',
    test: (pwd) => /[0-9]/.test(pwd),
  },
  {
    id: 'special',
    labelKey: 'password.reqSpecial',
    test: (pwd) => /[^A-Za-z0-9]/.test(pwd),
  },
]

/** Matches PasswordStrengthChecker scoring used in Account Settings. */
export function getPasswordStrength(password: string): PasswordStrength {
  const met = PASSWORD_REQUIREMENTS.filter((req) => req.test(password)).length
  const percentage = (met / PASSWORD_REQUIREMENTS.length) * 100
  if (percentage >= 80) return 'very-strong'
  if (percentage >= 60) return 'strong'
  if (percentage >= 40) return 'medium'
  return 'weak'
}

/** Account Settings rejects strength === 'weak' (needs ≥2 of 5 requirements). */
export function isPasswordStrongEnough(password: string): boolean {
  return password.length >= 8 && getPasswordStrength(password) !== 'weak'
}
